import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Dumbbell,
    TrendingUp,
    Calendar,
    Clock,
    ChevronRight,
    Trophy,
    Activity,
    Plus,
    Users,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export function AthleteDashboardPage() {
    const { user, profile } = useAuth();
    const navigate = useNavigate();

    // Fetch active prescriptions
    const { data: prescriptions, isLoading: loadingPrescriptions } = useQuery({
        queryKey: ['athlete-prescriptions', user?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('workout_prescriptions')
                .select(`
                    *,
                    workout:workouts(
                        name,
                        description,
                        estimated_duration_minutes
                    )
                `)
                .eq('student_id', user?.id)
                .eq('is_active', true);

            if (error) throw error;
            return data;
        },
        enabled: !!user?.id,
    });

    // Fetch weekly stats (simple count for now)
    const { data: weeklyStats } = useQuery({
        queryKey: ['athlete-weekly-stats', user?.id],
        queryFn: async () => {
            const lastWeek = new Date();
            lastWeek.setDate(lastWeek.getDate() - 7);

            const { count, error } = await supabase
                .from('workout_logs')
                .select('*', { count: 'exact', head: true })
                .eq('student_id', user?.id)
                .gte('completed_at', lastWeek.toISOString());

            if (error) throw error;
            return { count };
        },
        enabled: !!user?.id,
    });

    // Fetch pending invites
    const { data: pendingInvites, refetch: refetchInvites } = useQuery({
        queryKey: ['athlete-invites', user?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('student_pairings')
                .select(`
                    id,
                    personal:personal_id (
                        full_name,
                        avatar_url
                    ),
                    tenant:tenant_id (
                        name
                    )
                `)
                .eq('student_id', user?.id)
                .eq('status', 'pending');

            if (error) throw error;
            return data;
        },
        enabled: !!user?.id,
    });

    const handleInviteAction = async (pairingId: string, action: 'accept' | 'decline') => {
        try {
            if (action === 'accept') {
                const { error } = await supabase
                    .from('student_pairings')
                    .update({
                        status: 'active',
                        paired_at: new Date().toISOString(),
                    })
                    .eq('id', pairingId);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('student_pairings')
                    .update({
                        status: 'removed',
                        removed_at: new Date().toISOString(),
                    })
                    .eq('id', pairingId);
                if (error) throw error;
            }
            refetchInvites();
        } catch (error) {
            console.error('Error handling invite:', error);
            alert('Erro ao processar convite.');
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-12">
            {/* Hero / Welcome */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 to-primary-800 p-8 text-white shadow-xl">
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">
                            Olá, {profile?.full_name?.split(' ')[0] || 'Atleta'}! 👋
                        </h1>
                        <p className="text-primary-100 max-w-md">
                            {weeklyStats?.count === 0
                                ? "Vamos começar a semana com tudo? Seu corpo agradece!"
                                : `Ótimo trabalho! Você já completou ${weeklyStats?.count} treinos nos últimos 7 dias.`}
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                            <p className="text-xs text-primary-200 uppercase font-bold tracking-wider mb-1">Status Semanal</p>
                            <div className="flex items-center gap-2">
                                <Trophy className="w-5 h-5 text-yellow-400" />
                                <span className="text-2xl font-bold">{weeklyStats?.count || 0} Treinos</span>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-48 h-48 bg-primary-400/20 rounded-full blur-2xl"></div>
            </div>

            {/* Pending Invites Alert */}
            {pendingInvites && pendingInvites.length > 0 && (
                <div className="space-y-4">
                    {pendingInvites.map((invite: any) => (
                        <div key={invite.id} className="animate-bounce-subtle bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-2xl p-6 shadow-sm">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-4 text-center sm:text-left">
                                    <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center text-amber-600">
                                        <Users className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-amber-900 dark:text-amber-100">Convite de Personal</h3>
                                        <p className="text-sm text-amber-700 dark:text-amber-300">
                                            <span className="font-bold">{invite.personal.full_name}</span> convidou você para treinar no <span className="font-bold">{invite.tenant.name}</span>.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <button
                                        onClick={() => handleInviteAction(invite.id, 'decline')}
                                        className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-800/50 rounded-xl transition-colors"
                                    >
                                        Recusar
                                    </button>
                                    <button
                                        onClick={() => handleInviteAction(invite.id, 'accept')}
                                        className="flex-1 sm:flex-none px-6 py-2 text-sm font-bold bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors shadow-lg shadow-amber-600/20"
                                    >
                                        Aceitar Convite
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid gap-8 lg:grid-cols-3">
                {/* Main: Active Workouts */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Dumbbell className="w-5 h-5 text-primary-600" />
                            Meus Treinos Prescritos
                        </h2>
                    </div>

                    {loadingPrescriptions ? (
                        <div className="grid gap-4">
                            {[1, 2].map((i) => (
                                <div key={i} className="h-32 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse"></div>
                            ))}
                        </div>
                    ) : prescriptions && prescriptions.length > 0 ? (
                        <div className="grid gap-4">
                            {prescriptions.map((p: any) => (
                                <div
                                    key={p.id}
                                    className="group relative overflow-hidden bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm hover:shadow-md transition-all duration-300"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                                <Activity className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-900 dark:text-white text-lg">{p.workout.name}</h3>
                                                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-4 h-4" />
                                                        {p.workout.estimated_duration_minutes || 45} min
                                                    </span>
                                                    <span>•</span>
                                                    <span>{p.frequency_per_week || 3}x por semana</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between mt-6">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1 max-w-[70%]">
                                            {p.workout.description || "Prepare-se para superar seus limites hoje!"}
                                        </p>
                                        <button
                                            onClick={() => navigate(`/atleta/treino/${p.id}`)}
                                            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 transition-colors shadow-lg shadow-primary-600/20"
                                        >
                                            Iniciar
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="card text-center py-16 bg-white dark:bg-gray-900 border-dashed border-2">
                            <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-300">
                                <Dumbbell className="w-10 h-10" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Nenhum treino prescrito</h3>
                            <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                                Fale com seu personal trainer para receber sua rotina de treinos personalizada.
                            </p>
                        </div>
                    )}
                </div>

                {/* Sidebar: Quick Actions & Evolution */}
                <div className="space-y-8">
                    {/* Quick Access */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Plus className="w-5 h-5 text-primary-600" />
                            Ações Rápidas
                        </h2>
                        <div className="grid gap-3">
                            <Link
                                to="/atleta/progresso"
                                className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-primary-200 transition-colors shadow-sm"
                            >
                                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-purple-600" />
                                </div>
                                <div className="text-left">
                                    <p className="font-bold text-gray-900 dark:text-white text-sm">Registrar Peso</p>
                                    <p className="text-xs text-gray-500">Acompanhe sua evolução</p>
                                </div>
                            </Link>
                            <Link
                                to="/atleta/check-in"
                                className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-primary-200 transition-colors shadow-sm"
                            >
                                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                                    <Calendar className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div className="text-left">
                                    <p className="font-bold text-gray-900 dark:text-white text-sm">Check-in Semanal</p>
                                    <p className="text-xs text-gray-500">Feedback para seu coach</p>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Simple Evolution Chart placeholder */}
                    <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm">
                        <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-primary-600" />
                            Seu Progresso
                        </h3>
                        <div className="h-48 flex items-end justify-between gap-2 px-2">
                            {[40, 70, 45, 90, 65, 80, 55].map((h, i) => (
                                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                                    <div
                                        style={{ height: `${h}%` }}
                                        className={`w-full max-w-[20px] rounded-t-lg bg-gradient-to-t ${i === 6 ? 'from-primary-500 to-primary-600' : 'from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700'}`}
                                    ></div>
                                    <span className="text-[10px] text-gray-400 font-medium">S{i + 1}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-6 text-center">
                            Você está treinando com mais consistência este mês!
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
